import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Button,
  Chip,
  useTheme,
  alpha,
  Fade,
  Badge,
  Tooltip,
  Popover
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  MoneyOff as DebtIcon,
  Build as MaintenanceIcon,
  Storefront as StorefrontIcon,
  Dashboard as DashboardIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useUserSettings } from '../contexts/UserSettingsContext';
import DebtNotifications from './debts/DebtNotifications';
import MaintenanceNotifications from './maintenance/MaintenanceNotifications';
import { useDebtNotifications } from '../hooks/useDebtNotifications';
import { useMaintenanceNotifications } from '../hooks/useMaintenanceNotifications';

interface ModernAppBarProps {
  currentUser: any;
  systemSettings: any;
  onMenuToggle: () => void;
  onLogout: () => void;
  isMobile: boolean;
  currentPageTitle?: string;
}

const ModernAppBar: React.FC<ModernAppBarProps> = ({
  currentUser,
  systemSettings,
  onMenuToggle,
  onLogout,
  isMobile,
  currentPageTitle
}) => {
  const theme = useTheme();
  const { settings, updateSetting } = useUserSettings();

  // إضافة CSS للأنيميشن
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.1); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchor, setNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [debtNotificationsAnchor, setDebtNotificationsAnchor] = useState<null | HTMLElement>(null);

  // حالة صورة المستخدم
  const [userProfileImage, setUserProfileImage] = useState<string>('');
  const [maintenanceNotificationsAnchor, setMaintenanceNotificationsAnchor] = useState<null | HTMLElement>(null);
  const [completedNotificationsAnchor, setCompletedNotificationsAnchor] = useState<null | HTMLElement>(null);

  // استخدام hook إشعارات الديون والصيانة
  const { summary: debtSummary } = useDebtNotifications();
  const { summary: maintenanceSummary } = useMaintenanceNotifications();

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchor(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchor(null);
  };

  const handleDebtNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDebtNotificationsAnchor(event.currentTarget);
  };

  const handleDebtNotificationsClose = () => {
    setDebtNotificationsAnchor(null);
  };

  const handleMaintenanceNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMaintenanceNotificationsAnchor(event.currentTarget);
  };

  const handleMaintenanceNotificationsClose = () => {
    setMaintenanceNotificationsAnchor(null);
  };

  const handleCompletedNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCompletedNotificationsAnchor(event.currentTarget);
  };

  const handleCompletedNotificationsClose = () => {
    setCompletedNotificationsAnchor(null);
  };

  const handleLogoutClick = () => {
    handleProfileMenuClose();
    onLogout();
  };

  // تحميل صورة المستخدم من localStorage
  useEffect(() => {
    const savedImage = localStorage.getItem('userProfileImage');
    if (savedImage) {
      setUserProfileImage(savedImage);
    }
  }, []);

  // تبديل الوضع الداكن
  const toggleTheme = () => {
    updateSetting('theme', settings.theme === 'light' ? 'dark' : 'light');
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: '100%',
        zIndex: theme.zIndex.drawer + 1,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        backdropFilter: 'blur(20px)',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
          zIndex: -1
        },
        // إضافة أنيميشن النبض للإشعارات المهمة
        '@keyframes pulse': {
          '0%': { transform: 'scale(1)' },
          '50%': { transform: 'scale(1.1)' },
          '100%': { transform: 'scale(1)' }
        }
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          minHeight: '64px !important',
          px: { xs: 2, sm: 3 }
        }}
      >
        {/* الجانب الأيمن - القائمة والشعار */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={onMenuToggle}
              sx={{
                ml: 2,
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {systemSettings.companyLogo && (
            <Fade in timeout={600}>
              <Box
                component="img"
                src={systemSettings.companyLogo}
                alt={systemSettings.companyName}
                sx={{
                  height: 40,
                  mr: 2,
                  display: { xs: 'none', sm: 'block' },
                  borderRadius: 1,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`
                }}
              />
            </Fade>
          )}

          <Box>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1rem', sm: '1.25rem' },
                textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.2)}`,
                background: `linear-gradient(45deg, ${theme.palette.common.white} 30%, ${alpha(theme.palette.common.white, 0.8)} 90%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              {currentPageTitle || systemSettings.companyName || 'نظام إدارة مركز الصيانة'}
            </Typography>

            {currentUser?.role && (
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.common.white, 0.8),
                  display: { xs: 'none', sm: 'block' },
                  fontSize: '0.75rem'
                }}
              >
                {currentUser.role}
              </Typography>
            )}
          </Box>
        </Box>

        {/* الجانب الأيسر - الإشعارات ومعلومات المستخدم */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* رابط لوحة المعلومات */}
          <Tooltip title="لوحة المعلومات" arrow>
            <IconButton
              component={RouterLink}
              to="/admin"
              color="inherit"
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <DashboardIcon />
            </IconButton>
          </Tooltip>

          {/* رابط المتجر */}
          <Tooltip title="المتجر الإلكتروني" arrow>
            <IconButton
              component={RouterLink}
              to="/store"
              color="inherit"
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <StorefrontIcon />
            </IconButton>
          </Tooltip>

          {/* تبديل الوضع الداكن */}
          <Tooltip title={settings.theme === 'light' ? 'الوضع الداكن' : 'الوضع الفاتح'} arrow>
            <IconButton
              onClick={toggleTheme}
              color="inherit"
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {settings.theme === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Tooltip>

          {/* إشعارات الصيانة - قيد الانتظار */}
          <Tooltip title="طلبات قيد الانتظار وقيد العمل" arrow>
            <IconButton
              color="inherit"
              onClick={handleMaintenanceNotificationsOpen}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Badge
                badgeContent={maintenanceSummary.pendingCount}
                color="warning"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: maintenanceSummary.pendingCount > 0 ? '#ff9800' : 'transparent',
                    color: 'white',
                    fontWeight: 'bold'
                  }
                }}
              >
                <MaintenanceIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* إشعارات الطلبات المكتملة */}
          <Tooltip title="طلبات تم الإصلاح (جاهزة للتسليم)" arrow>
            <IconButton
              color="inherit"
              onClick={handleCompletedNotificationsOpen}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Badge
                badgeContent={maintenanceSummary.completedCount}
                color="success"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: maintenanceSummary.completedCount > 0 ? '#4caf50' : 'transparent',
                    color: 'white',
                    fontWeight: 'bold'
                  }
                }}
              >
                <CheckCircleIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* إشعارات الديون */}
          <Tooltip
            title={`إشعارات الديون (${debtSummary.totalCount} إجمالي، ${debtSummary.overdueCount} متأخر)`}
            arrow
          >
            <IconButton
              color="inherit"
              onClick={handleDebtNotificationsOpen}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Badge
                badgeContent={debtSummary.totalCount}
                color="error"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: debtSummary.totalCount > 0 ? '#f44336' : 'transparent',
                    color: 'white',
                    fontWeight: 'bold',
                    animation: debtSummary.overdueCount > 0 ? 'pulse 2s infinite' : 'none'
                  }
                }}
              >
                <DebtIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* إشعارات عامة */}
          <Tooltip title="الإشعارات العامة" arrow>
            <IconButton
              color="inherit"
              onClick={handleNotificationsOpen}
              sx={{
                bgcolor: alpha(theme.palette.common.white, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Badge badgeContent={3} color="info" variant="dot">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* معلومات المستخدم */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.common.white,
                  fontWeight: 600,
                  fontSize: '0.875rem'
                }}
              >
                مرحباً، {currentUser?.name || 'المستخدم'}
              </Typography>
              <Chip
                label={currentUser?.role || 'مدير النظام'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  color: theme.palette.common.white,
                  border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`
                }}
              />
            </Box>

            <Button
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                p: 0.5,
                minWidth: 'auto',
                '&:hover': {
                  bgcolor: alpha(theme.palette.common.white, 0.1),
                  transform: 'scale(1.05)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              <Avatar
                src={userProfileImage}
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: alpha(theme.palette.common.white, 0.2),
                  color: theme.palette.primary.main,
                  fontWeight: 'bold',
                  border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`,
                  boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`
                }}
              >
                {!userProfileImage && (currentUser?.name?.charAt(0) || 'U')}
              </Avatar>
            </Button>
          </Box>
        </Box>

        {/* قائمة الملف الشخصي */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 200,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '& .MuiMenuItem-root': {
                borderRadius: 1,
                mx: 1,
                my: 0.5,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08)
                }
              }
            }
          }}
        >
          <MenuItem
            component={RouterLink}
            to="/admin/profile"
            onClick={handleProfileMenuClose}
          >
            <PersonIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
            الملف الشخصي
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to="/admin/account-settings"
            onClick={handleProfileMenuClose}
          >
            <SettingsIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
            إعدادات الحساب
          </MenuItem>
          <MenuItem onClick={handleLogoutClick}>
            <LogoutIcon sx={{ mr: 2, color: theme.palette.error.main }} />
            تسجيل الخروج
          </MenuItem>
        </Menu>

        {/* قائمة الإشعارات العامة */}
        <Menu
          anchorEl={notificationsAnchor}
          open={Boolean(notificationsAnchor)}
          onClose={handleNotificationsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 2,
              minWidth: 300,
              maxHeight: 400,
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.2)}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              📢 الإشعارات العامة
            </Typography>
            <Typography variant="body2" color="text.secondary">
              لا توجد إشعارات جديدة
            </Typography>
          </Box>
        </Menu>

        {/* قائمة إشعارات الديون */}
        <Popover
          anchorEl={debtNotificationsAnchor}
          open={Boolean(debtNotificationsAnchor)}
          onClose={handleDebtNotificationsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
            }
          }}
        >
          <DebtNotifications
            onClose={handleDebtNotificationsClose}
            maxItems={8}
          />
        </Popover>

        {/* Popover إشعارات الصيانة - قيد الانتظار */}
        <Popover
          open={Boolean(maintenanceNotificationsAnchor)}
          anchorEl={maintenanceNotificationsAnchor}
          onClose={handleMaintenanceNotificationsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }
            }
          }}
        >
          <MaintenanceNotifications
            onClose={handleMaintenanceNotificationsClose}
            maxItems={8}
            filterStatus="pending"
          />
        </Popover>

        {/* Popover إشعارات الطلبات المكتملة */}
        <Popover
          open={Boolean(completedNotificationsAnchor)}
          anchorEl={completedNotificationsAnchor}
          onClose={handleCompletedNotificationsClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden'
              }
            }
          }}
        >
          <MaintenanceNotifications
            onClose={handleCompletedNotificationsClose}
            maxItems={8}
            filterStatus="completed"
          />
        </Popover>
      </Toolbar>
    </AppBar>
  );
};

export default ModernAppBar;
